import { Injectable, computed, inject, signal } from '@angular/core';
import { AuthorizationService } from '../auth';

/** Global bridge key for cross-MFE userTypeId sharing */
const USER_TYPE_GLOBAL_KEY = '__SYMPHONY_USER_TYPE_ID__';

@Injectable({ providedIn: 'root' })
export class UserContextService {
  private readonly authService = inject(AuthorizationService);

  /** Helper to read current value from globalThis across MFEs dynamically */
  private static getGlobalUserTypeId(): number | null {
    return ((globalThis as Record<string, unknown>)[USER_TYPE_GLOBAL_KEY] as number | null) ?? null;
  }

  /** UserTypeId set by the consuming app after fetching from API */
  private readonly _userTypeId = signal<number | null>(UserContextService.getGlobalUserTypeId());

  /** Public readonly signal for userTypeId with runtime bridge sync */
  readonly userTypeId = computed<number | null>(() => {
    return this._userTypeId() ?? UserContextService.getGlobalUserTypeId();
  });

  /** Raw vendorIds extracted from JWT claims */
  readonly vendorIds = computed<number[]>(() => {
    const claims = this.authService.context().claims;
    const raw = claims['vendorIds'];
    if (Array.isArray(raw)) {
      return raw.filter((v): v is number => typeof v === 'number');
    }
    if (typeof raw === 'number') {
      return [raw];
    }
    return [];
  });

  /** User permissions from JWT */
  readonly permissions = computed<readonly string[]>(() => this.authService.context().permissions);

  /** Whether userTypeId has been resolved (loaded or explicitly set) */
  private readonly _isUserTypeLoaded = signal(UserContextService.getGlobalUserTypeId() != null);
  readonly isUserTypeLoaded = computed<boolean>(() => {
    return this._isUserTypeLoaded() || UserContextService.getGlobalUserTypeId() != null;
  });

  /** Set userTypeId - called by the app after fetching from API */
  setUserTypeId(userTypeId: number | null): void {
    this._userTypeId.set(userTypeId);
    this._isUserTypeLoaded.set(true);
    // Bridge: store on globalThis so MFE instances (different DI root) can pick it up
    (globalThis as Record<string, unknown>)[USER_TYPE_GLOBAL_KEY] = userTypeId;
  }

  hasPermission(permission: string): boolean {
    return this.authService.context().permissions.includes(permission);
  }
}
